/**
 * 프로젝트 시드 — `project`, `project_detail`, `project_phase`(+`phase_log`), `project_member`.
 *
 * 프로젝트 본체는 뼈대만 담는다. 용도·규모·면적·공사비 같은 사무소 업무 지식은 상세 항목
 * (`project_detail`)이고, 항목 정의는 사무소가 더할 수 있다 — 주소와 사용승인은 기본 항목이
 * 아니라 이 사무소가 더한 것으로 둔다.
 *
 * 단계는 행이고 순서(`ordinal`)를 가진다. 날짜는 단계 행에 있다 — 시작일, 완료예정일, 완료일
 * 셋이다. PM 은 투입 인원의 `pm` 배치가 정본이고 한 프로젝트에 둘일 수 있다.
 */
import type { DetailField, DetailValueType, PhaseLog, ProjectStatus } from "@/types";

export interface MockProject {
  id: string;
  code: string | null;
  name: string;
  color: string;
  status: ProjectStatus;
  started_on: string | null;
  ended_on: string | null;
  budget_hours: number | null;
  memo: string;
  nas_path: string;
}

export interface MockPhase {
  id: string;
  project_id: string;
  name: string;
  template_key: string;
  ordinal: number;
  sort_order: number;
  start_label: string;
  end_label: string;
  start_date: string | null;
  due_date: string | null;
  done_date: string | null;
  logs: PhaseLog[];
}

export interface MockMember {
  id: string;
  project_id: string;
  user_id: string;
  assigned_on: string;
  released_on: string | null;
  /** 이 프로젝트의 PM 인가 — PM 은 사람이 아니라 배치에 붙는 이름표다 */
  is_pm: boolean;
}

export interface MockMemberPlan {
  id: string;
  project_member_id: string;
  period_from: string;
  period_to: string;
  /** 소수 둘째 자리까지 — 서버가 문자열로 준다 */
  daily_hours: string;
  note: string;
}

export interface MockDetail {
  project_id: string;
  field_id: string;
  value_text: string | null;
  value_number: number | null;
  value_date: string | null;
}

/* ── 상세 항목 정의 ────────────────────────────────────────────────────── */

const field = (
  key: string,
  label: string,
  value_type: DetailValueType,
  unit = "",
  options: { filterable?: boolean; seed?: boolean } = {}
): DetailField => ({
  id: `field-${key}`,
  key,
  label,
  value_type,
  unit,
  sort_order: 0,
  is_filterable: options.filterable ?? false,
  is_seed: options.seed ?? true,
});

/** 설치할 때 깔리는 아홉 항목과, 이 사무소가 더한 둘(주소·사용승인). */
export const detailFields: DetailField[] = [
  field("usage", "용도", "text", "", { filterable: true }),
  field("scale", "규모", "text"),
  field("work_scope", "업무범위", "text", "", { filterable: true }),
  field("building_area", "건축면적", "area", "제곱미터"),
  field("total_floor_area", "연면적", "area", "제곱미터"),
  field("construction_area", "공사면적", "area", "제곱미터"),
  field("construction_cost", "공사비", "money", "원"),
  field("constructor", "시공사", "text"),
  field("construction_started_on", "착공신고", "date"),
  field("address", "주소", "text", "", { seed: false }),
  field("use_approved_on", "사용승인", "date", "", { seed: false }),
].map((row, index) => ({ ...row, sort_order: (index + 1) * 10 }));

const FIELD_TYPE = new Map(detailFields.map((row) => [row.key, row.value_type]));

/* ── 프로젝트 ──────────────────────────────────────────────────────────── */

interface ProjectSeed extends Omit<
  MockProject,
  "started_on" | "ended_on" | "budget_hours" | "memo" | "nas_path"
> {
  started_on?: string;
  ended_on?: string;
  budget_hours?: number;
  memo?: string;
  /** 상세 항목 값 — 항목 정의의 key 로 적는다 */
  details: Record<string, string | number>;
  /** 계약에 적는 발주처 이름 */
  client: string;
  pm: string;
}

/** 코드는 `YY-NNN`. 진행 7개, 착수예정 2개, 완료 2개, 보류 1개. */
const SEED: ProjectSeed[] = [
  {
    id: "p-2511",
    code: "25-011",
    name: "성수동 대수선",
    color: "#3182f6",
    status: "active",
    started_on: "2025-11-03",
    budget_hours: 1800,
    memo: "기존 보 보강 포함",
    client: "성수개발",
    pm: "u-pm",
    details: {
      usage: "근린생활시설",
      work_scope: "설계",
      scale: "지하 1층, 지상 4층",
      building_area: 182.4,
      total_floor_area: 642.3,
      construction_area: 642.3,
      address: "서울 마포구 망원동 4xx-x",
    },
  },
  {
    id: "p-2512",
    code: "25-012",
    name: "덕은 공동주택",
    color: "#a234c7",
    status: "active",
    started_on: "2025-12-01",
    budget_hours: 6200,
    client: "(주)덕은건설",
    pm: "u-pm2",
    details: {
      usage: "공동주택",
      work_scope: "설계",
      scale: "지하 2층, 지상 15층, 2개 동",
      building_area: 1840.2,
      total_floor_area: 21480.6,
      address: "경기 고양시 덕양구 행신동 2xx",
    },
  },
  {
    id: "p-2601",
    code: "26-001",
    name: "양평 단독주택",
    color: "#03b26c",
    status: "active",
    started_on: "2026-01-12",
    budget_hours: 600,
    client: "김하늘 건축주",
    pm: "u-pm2",
    details: {
      usage: "단독주택",
      work_scope: "설계",
      scale: "지상 2층",
      building_area: 96.8,
      total_floor_area: 168.5,
      address: "경기 양평군 서종면 문호리 5xx",
    },
  },
  {
    id: "p-2603",
    code: "26-003",
    name: "망원 수변 산책로",
    color: "#18a5a5",
    status: "active",
    started_on: "2026-03-02",
    budget_hours: 1400,
    client: "성동구청 공원녹지과",
    pm: "u-pm",
    details: {
      usage: "공원",
      work_scope: "설계, 감리",
      scale: "연장 1.2km",
      address: "서울 성동구 응봉동 일대",
    },
  },
  {
    id: "p-2604",
    code: "26-004",
    name: "판교 사옥 신축",
    color: "#fe9800",
    status: "active",
    started_on: "2026-04-06",
    budget_hours: 5200,
    client: "누리홀딩스",
    pm: "u-pm2",
    details: {
      usage: "업무시설",
      work_scope: "설계, 감리",
      scale: "지하 3층, 지상 9층",
      building_area: 612.5,
      total_floor_area: 6820.4,
      address: "서울 성동구 성수동2가 3xx-x",
    },
  },
  {
    id: "p-2605",
    code: "26-005",
    name: "연남동 근린생활시설",
    color: "#f04452",
    status: "active",
    started_on: "2026-05-11",
    budget_hours: 1500,
    client: "이정훈 건축주",
    pm: "u-pm3",
    details: {
      usage: "근린생활시설",
      work_scope: "설계",
      scale: "지하 1층, 지상 5층",
      building_area: 210.3,
      total_floor_area: 961.85,
      address: "서울 용산구 한남동 6xx-x",
    },
  },
  {
    id: "p-2606",
    code: "26-006",
    name: "해솔 어린이집 리모델링",
    color: "#03b26c",
    status: "active",
    started_on: "2026-06-01",
    budget_hours: 1100,
    client: "광진구청 보육지원과",
    pm: "u-pm3",
    details: {
      usage: "노유자시설",
      work_scope: "설계, 감리",
      scale: "지상 3층",
      building_area: 320.1,
      total_floor_area: 884,
      construction_area: 884,
      construction_cost: 1_380_000_000,
      constructor: "우리종합건설",
      construction_started_on: "2026-09-25",
      address: "서울 광진구 자양동 2xx",
    },
  },
  {
    id: "p-2607",
    code: "26-007",
    name: "이천 물류센터",
    color: "#a234c7",
    status: "active",
    started_on: "2026-06-22",
    budget_hours: 3800,
    client: "한빛로지스",
    pm: "u-pm3",
    details: {
      usage: "창고시설",
      work_scope: "설계",
      scale: "지상 3층",
      building_area: 8420,
      total_floor_area: 24120,
      address: "경기 이천시 마장면 표교리 1xx",
    },
  },
  {
    id: "p-2608",
    code: "26-008",
    name: "솔바람 카페",
    color: "#fe9800",
    status: "planned",
    memo: "계약 전 — 11월 초 계약 예정",
    client: "박민서 건축주",
    pm: "u-pm",
    details: {
      usage: "근린생활시설",
      work_scope: "설계",
      scale: "지상 2층",
      address: "강원 강릉시 사천면 해안로 xx",
    },
  },
  {
    id: "p-2609",
    code: "26-009",
    name: "세종 시립도서관 현상설계",
    color: "#18a5a5",
    status: "planned",
    client: "하남시 도서관과",
    pm: "u-pm2",
    details: { usage: "문화시설", work_scope: "현상설계", address: "경기 하남시 미사동 일대" },
  },
  {
    id: "p-2410",
    code: "24-010",
    name: "덕은 견본주택",
    color: "#3182f6",
    status: "done",
    started_on: "2024-10-07",
    ended_on: "2025-06-30",
    client: "(주)덕은건설",
    pm: "u-pm2",
    details: {
      usage: "판매시설",
      work_scope: "설계, 감리",
      construction_cost: 2_100_000_000,
      use_approved_on: "2025-06-20",
      address: "경기 고양시 일산동구 장항동 8xx",
    },
  },
  {
    id: "p-2502",
    code: "25-002",
    name: "새빛교회 증축",
    color: "#f04452",
    status: "done",
    started_on: "2025-02-17",
    ended_on: "2026-08-28",
    client: "새빛교회",
    pm: "u-pm",
    details: {
      usage: "종교시설",
      work_scope: "설계, 감리",
      construction_cost: 940_000_000,
      constructor: "우리종합건설",
      construction_started_on: "2026-02-27",
      use_approved_on: "2026-08-26",
      address: "서울 은평구 불광동 3xx",
    },
  },
];

export const projects: MockProject[] = SEED.map((seed) => ({
  id: seed.id,
  code: seed.code,
  name: seed.name,
  color: seed.color,
  status: seed.status,
  started_on: seed.started_on ?? null,
  ended_on: seed.ended_on ?? null,
  budget_hours: seed.budget_hours ?? null,
  memo: seed.memo ?? "",
  nas_path: seed.code ? `/projects/${seed.code}` : "",
}));

/** 발주처 이름 — 계약에 적는다. 거래처 테이블은 없다. */
export const CLIENT_NAMES: Record<string, string> = Object.fromEntries(
  SEED.map((seed) => [seed.id, seed.client])
);

export const projectDetails: MockDetail[] = SEED.flatMap((seed) =>
  Object.entries(seed.details).map(([key, value]) => {
    const type = FIELD_TYPE.get(key);
    return {
      project_id: seed.id,
      field_id: `field-${key}`,
      value_text: type === "text" ? String(value) : null,
      value_number: type === "number" || type === "area" || type === "money" ? Number(value) : null,
      value_date: type === "date" ? String(value) : null,
    };
  })
);

/* ── 단계 ──────────────────────────────────────────────────────────────── */

/** 설치할 때 깔리는 기본 단계. 이름표와 날짜 칸 이름은 backend `PHASE_TEMPLATE_SEED` 와 같다. */
const TEMPLATE = [
  { key: "pre_design", name: "기획설계", start_label: "계약일", end_label: "완료일" },
  { key: "basic_design", name: "기본설계", start_label: "시작일", end_label: "완료일" },
  { key: "permit", name: "허가접수", start_label: "접수일", end_label: "완료일" },
  { key: "working_design", name: "실시설계", start_label: "종료예정일", end_label: "종료일" },
  { key: "design_change", name: "설계변경", start_label: "종료예정일", end_label: "종료일" },
  { key: "supervision", name: "감리", start_label: "시작일", end_label: "사용승인" },
] as const;

type TemplateName = (typeof TEMPLATE)[number]["name"];

interface PhaseSetup {
  skip?: TemplateName[];
  /** 단계별 용역비(원) — 계약의 phase_fee 로 들어간다 */
  fee?: Partial<Record<string, number>>;
  insert?: { after: TemplateName; name: string; start_label: string; end_label: string }[];
}

const SETUP: Record<string, PhaseSetup> = {
  // 대수선이라 허가 대신 신고 — 허가접수 단계를 뺐다.
  "p-2511": {
    skip: ["허가접수"],
    fee: { 기획설계: 12_000_000, 기본설계: 58_000_000, 실시설계: 96_000_000, 설계변경: 20_000_000 },
  },
  "p-2512": {
    fee: {
      기획설계: 40_000_000,
      기본설계: 180_000_000,
      허가접수: 150_000_000,
      실시설계: 300_000_000,
      감리: 72_000_000,
    },
  },
  "p-2601": {
    skip: ["감리"],
    fee: { 기획설계: 8_000_000, 기본설계: 18_000_000, 허가접수: 10_000_000, 실시설계: 22_000_000 },
  },
  // 공원은 허가 앞에 구청 심의가 따로 있다 — 사이에 끼운 단계.
  "p-2603": {
    skip: ["설계변경"],
    fee: { 기본설계: 64_000_000, 허가접수: 20_000_000, 실시설계: 90_000_000, 감리: 40_500_000 },
    insert: [
      { after: "기본설계", name: "공원조성계획 심의", start_label: "상정일", end_label: "의결일" },
    ],
  },
  "p-2604": {
    fee: {
      기획설계: 60_000_000,
      기본설계: 280_000_000,
      허가접수: 200_000_000,
      실시설계: 450_000_000,
      감리: 130_000_000,
    },
  },
  "p-2605": {
    skip: ["감리"],
    fee: { 기획설계: 10_000_000, 기본설계: 30_000_000, 허가접수: 16_000_000, 실시설계: 40_000_000 },
  },
  "p-2606": {
    skip: ["설계변경"],
    fee: { 기본설계: 18_000_000, 허가접수: 6_000_000, 실시설계: 27_000_000, 감리: 22_400_000 },
  },
  // 층고 상향으로 설계변경을 두 번 한다 — 2차를 뒤에 끼운다.
  "p-2607": {
    skip: ["감리"],
    fee: {
      기획설계: 30_000_000,
      기본설계: 110_000_000,
      허가접수: 60_000_000,
      실시설계: 150_000_000,
      설계변경: 38_000_000,
    },
    insert: [
      { after: "설계변경", name: "설계변경 2차", start_label: "시작일", end_label: "종료일" },
    ],
  },
  "p-2608": {},
  "p-2609": {
    skip: ["허가접수", "실시설계", "설계변경", "감리"],
    insert: [{ after: "기본설계", name: "작품 제출", start_label: "접수일", end_label: "발표일" }],
  },
  "p-2410": { fee: { 기본설계: 40_000_000, 실시설계: 60_000_000, 감리: 30_000_000 } },
  "p-2502": {
    skip: ["설계변경"],
    fee: { 기본설계: 26_000_000, 허가접수: 8_000_000, 실시설계: 34_000_000, 감리: 18_000_000 },
  },
};

/** 단계 id — 프로젝트와 단계 이름으로 만든다(시드에서 찾기 쉽게). */
export const phaseId = (projectId: string, name: string) => `ph-${projectId.slice(2)}-${name}`;

/**
 * 단계 날짜 — 시작일, 완료예정일, 완료일. 몇 곳은 완료예정일을 넘겼다(지연 표시의 재료).
 *  - 양평 단독주택 기본설계: 10/16 예정인데 아직 진행 중
 *  - 덕은 공동주택 기본설계: 8/14 예정, 8/21 완료
 */
const DATES: {
  project: string;
  name: string;
  start: string | null;
  due: string;
  done: string | null;
}[] = [
  {
    project: "p-2511",
    name: "기획설계",
    start: "2025-11-03",
    due: "2025-12-19",
    done: "2025-12-19",
  },
  {
    project: "p-2511",
    name: "기본설계",
    start: "2026-06-01",
    due: "2026-09-11",
    done: "2026-09-11",
  },
  { project: "p-2511", name: "실시설계", start: "2026-09-14", due: "2026-10-30", done: null },
  // 겹치는 단계 — 실시설계를 마무리하는 중에 설계변경이 걸리고, 그 사이 착공해 감리가 붙었다.
  // 셋이 한 달 남짓 같이 간다. 공정표에서 단계가 층으로 쌓이는 경우다.
  { project: "p-2511", name: "설계변경", start: "2026-10-05", due: "2026-11-27", done: null },
  { project: "p-2511", name: "감리", start: "2026-10-19", due: "2027-01-29", done: null },
  {
    project: "p-2512",
    name: "기본설계",
    start: "2026-03-02",
    due: "2026-08-14",
    done: "2026-08-21",
  },
  { project: "p-2512", name: "허가접수", start: "2026-08-24", due: "2026-10-30", done: null },
  { project: "p-2512", name: "실시설계", start: null, due: "2027-02-26", done: null },
  {
    project: "p-2601",
    name: "기획설계",
    start: "2026-07-06",
    due: "2026-09-18",
    done: "2026-09-18",
  },
  { project: "p-2601", name: "기본설계", start: "2026-09-21", due: "2026-10-16", done: null },
  {
    project: "p-2603",
    name: "기본설계",
    start: "2026-03-02",
    due: "2026-08-28",
    done: "2026-08-28",
  },
  {
    project: "p-2603",
    name: "공원조성계획 심의",
    start: "2026-09-07",
    due: "2026-11-05",
    done: null,
  },
  // 심의 의결을 기다리면서 허가 서류를 넣었다 — 둘이 3주쯤 겹친다.
  { project: "p-2603", name: "허가접수", start: "2026-10-12", due: "2026-12-18", done: null },
  {
    project: "p-2604",
    name: "기획설계",
    start: "2026-04-06",
    due: "2026-08-28",
    done: "2026-08-28",
  },
  { project: "p-2604", name: "기본설계", start: "2026-09-01", due: "2026-10-30", done: null },
  {
    project: "p-2605",
    name: "허가접수",
    start: "2026-06-15",
    due: "2026-08-14",
    done: "2026-08-13",
  },
  { project: "p-2605", name: "실시설계", start: "2026-08-17", due: "2026-10-23", done: null },
  {
    project: "p-2606",
    name: "실시설계",
    start: "2026-07-20",
    due: "2026-09-25",
    done: "2026-09-25",
  },
  { project: "p-2606", name: "감리", start: "2026-09-28", due: "2026-12-18", done: null },
  {
    project: "p-2607",
    name: "기본설계",
    start: "2026-07-06",
    due: "2026-10-02",
    done: "2026-10-02",
  },
  { project: "p-2607", name: "설계변경", start: "2026-10-05", due: "2026-11-06", done: null },
  { project: "p-2607", name: "설계변경 2차", start: null, due: "2026-12-11", done: null },
  { project: "p-2502", name: "감리", start: "2026-03-02", due: "2026-08-28", done: "2026-08-26" },
  { project: "p-2608", name: "기획설계", start: null, due: "2026-12-11", done: null },
  { project: "p-2609", name: "기본설계", start: "2026-10-19", due: "2026-11-13", done: null },
  { project: "p-2609", name: "작품 제출", start: null, due: "2026-12-04", done: null },
];

/**
 * 단계 진행 기록 — 마일스톤을 대신한다. 날짜와 한 줄, 그리고 예정인지 완료인지.
 * 예정으로 남은 기록이 「다가오는 일정」이고, 날짜가 지나면 밀린 것이다.
 */
const LOGS: {
  project: string;
  phase: string;
  date: string;
  status: "planned" | "done";
  text: string;
  author: string;
}[] = [
  {
    project: "p-2511",
    phase: "기획설계",
    date: "2026-09-04",
    status: "done",
    text: "착공 전 현황 조사",
    author: "u-pm",
  },
  {
    project: "p-2511",
    phase: "실시설계",
    date: "2026-10-08",
    status: "done",
    text: "발주처 설계 협의",
    author: "u-pm",
  },
  {
    project: "p-2511",
    phase: "실시설계",
    date: "2026-10-30",
    status: "planned",
    text: "1차 납품(건축 도면)",
    author: "u-pm",
  },
  {
    project: "p-2512",
    phase: "허가접수",
    date: "2026-09-22",
    status: "done",
    text: "건축허가 접수",
    author: "u-pm2",
  },
  {
    project: "p-2512",
    phase: "허가접수",
    date: "2026-10-23",
    status: "planned",
    text: "허가 보완 도서 제출",
    author: "u-pm2",
  },
  {
    project: "p-2512",
    phase: "허가접수",
    date: "2026-10-28",
    status: "planned",
    text: "건축허가 득",
    author: "u-pm2",
  },
  {
    project: "p-2601",
    phase: "기본설계",
    date: "2026-10-16",
    status: "planned",
    text: "기본설계 건축주 보고",
    author: "u-pm2",
  },
  {
    project: "p-2603",
    phase: "공원조성계획 심의",
    date: "2026-10-14",
    status: "done",
    text: "구청 보완서류 제출",
    author: "u-pm",
  },
  {
    project: "p-2603",
    phase: "공원조성계획 심의",
    date: "2026-11-05",
    status: "planned",
    text: "공원조성계획 심의 발표",
    author: "u-pm",
  },
  {
    project: "p-2604",
    phase: "기본설계",
    date: "2026-10-14",
    status: "done",
    text: "기본설계 중간보고",
    author: "u-pm2",
  },
  {
    project: "p-2604",
    phase: "기본설계",
    date: "2026-10-23",
    status: "planned",
    text: "기계실 대안 협의",
    author: "u-pm2",
  },
  {
    project: "p-2605",
    phase: "실시설계",
    date: "2026-10-23",
    status: "planned",
    text: "실시설계도서 납품",
    author: "u-pm3",
  },
  {
    project: "p-2606",
    phase: "감리",
    date: "2026-09-28",
    status: "done",
    text: "착공",
    author: "u-pm3",
  },
  {
    project: "p-2606",
    phase: "감리",
    date: "2026-10-21",
    status: "done",
    text: "골조 공사 감리 점검",
    author: "u-pm3",
  },
  {
    project: "p-2606",
    phase: "감리",
    date: "2026-10-28",
    status: "planned",
    text: "2층 슬래브 타설 검측",
    author: "u-pm3",
  },
  {
    project: "p-2607",
    phase: "설계변경",
    date: "2026-10-13",
    status: "done",
    text: "랙 배치 협의",
    author: "u-pm3",
  },
  {
    project: "p-2607",
    phase: "설계변경",
    date: "2026-11-06",
    status: "planned",
    text: "설계변경 도서 제출",
    author: "u-pm3",
  },
  {
    project: "p-2609",
    phase: "기획설계",
    date: "2026-10-22",
    status: "planned",
    text: "현장 설명회",
    author: "u-pm2",
  },
  {
    project: "p-2609",
    phase: "기획설계",
    date: "2026-10-29",
    status: "planned",
    text: "질의 회신 마감",
    author: "u-pm2",
  },
];

export const projectPhases: MockPhase[] = SEED.flatMap((seed) => {
  const setup = SETUP[seed.id] ?? {};
  const list: { key: string; name: string; start_label: string; end_label: string }[] =
    TEMPLATE.filter((row) => !setup.skip?.includes(row.name)).map((row) => ({
      key: row.key as string,
      name: row.name as string,
      start_label: row.start_label as string,
      end_label: row.end_label as string,
    }));
  for (const insert of setup.insert ?? []) {
    const at = list.findIndex((row) => row.name === insert.after);
    list.splice(at + 1, 0, {
      key: "",
      name: insert.name,
      start_label: insert.start_label,
      end_label: insert.end_label,
    });
  }
  return list.map((row, index) => {
    const dates = DATES.find((item) => item.project === seed.id && item.name === row.name);
    return {
      id: phaseId(seed.id, row.name),
      project_id: seed.id,
      name: row.name,
      template_key: row.key,
      ordinal: index + 1,
      sort_order: (index + 1) * 100,
      start_label: row.start_label,
      end_label: row.end_label,
      start_date: dates?.start ?? null,
      due_date: dates?.due ?? null,
      done_date: dates?.done ?? null,
      logs: LOGS.filter((log) => log.project === seed.id && log.phase === row.name).map(
        (log, position) => ({
          id: `log-${seed.id}-${row.name}-${position}`,
          log_date: log.date,
          status: log.status,
          text: log.text,
          author_id: log.author,
          created_at: `${log.date}T10:00:00+09:00`,
        })
      ),
    };
  });
});

/** 그날 진행 중이던 단계의 이름 — 시작했고 아직 끝나지 않은 단계. */
export function phaseNameOn(projectId: string, date: string): string | null {
  const phase = projectPhases.find(
    (row) =>
      row.project_id === projectId &&
      row.start_date !== null &&
      row.start_date <= date &&
      (row.done_date === null || date <= row.done_date)
  );
  return phase?.name ?? null;
}

/* ── 투입 인원 ─────────────────────────────────────────────────────────── */

/**
 * 누가 어느 프로젝트에 언제부터 배치되었나. `to` 가 있으면 그날 해제됐다 — 퇴사, 휴직한 사람.
 * Daily 시드도 이 표를 읽는다 — 한곳에서 정해야 어긋나지 않는다.
 */
export const TEAMS: { project_id: string; user_id: string; from: string; to?: string }[] = [
  { project_id: "p-2511", user_id: "u-pm", from: "2025-11-03" },
  { project_id: "p-2511", user_id: "u-staff", from: "2025-11-03" },
  { project_id: "p-2511", user_id: "u-st4", from: "2026-02-02" },
  { project_id: "p-2511", user_id: "u-pm3", from: "2026-03-16", to: "2026-10-09" },
  { project_id: "p-2511", user_id: "변경민", from: "2025-11-03" },
  { project_id: "p-2512", user_id: "u-pm2", from: "2025-12-01" },
  { project_id: "p-2512", user_id: "u-st2", from: "2025-12-01" },
  { project_id: "p-2601", user_id: "u-pm2", from: "2026-01-12" },
  { project_id: "p-2601", user_id: "u-st2", from: "2026-01-12" },
  { project_id: "p-2601", user_id: "u-st6", from: "2026-01-12", to: "2026-09-30" },
  { project_id: "p-2603", user_id: "u-pm", from: "2026-03-02" },
  { project_id: "p-2603", user_id: "u-staff", from: "2026-03-02" },
  { project_id: "p-2603", user_id: "u-st5", from: "2026-07-06" },
  { project_id: "p-2604", user_id: "u-pm2", from: "2026-04-06" },
  { project_id: "p-2604", user_id: "u-st2", from: "2026-04-06" },
  { project_id: "p-2604", user_id: "u-st5", from: "2026-07-06" },
  { project_id: "p-2605", user_id: "u-pm3", from: "2026-05-11", to: "2026-10-09" },
  { project_id: "p-2605", user_id: "u-pm", from: "2026-05-11" },
  { project_id: "p-2605", user_id: "u-st3", from: "2026-05-11" },
  { project_id: "p-2606", user_id: "u-pm3", from: "2026-06-01", to: "2026-10-09" },
  { project_id: "p-2606", user_id: "u-st3", from: "2026-06-01" },
  { project_id: "p-2606", user_id: "u-st5", from: "2026-07-06" },
  { project_id: "p-2607", user_id: "u-pm3", from: "2026-06-22", to: "2026-10-09" },
  { project_id: "p-2607", user_id: "u-st4", from: "2026-06-22" },
  { project_id: "p-2608", user_id: "u-pm", from: "2026-11-02" },
  { project_id: "p-2609", user_id: "u-pm2", from: "2026-10-19" },
  { project_id: "p-2609", user_id: "u-st3", from: "2026-10-19" },
  { project_id: "p-2410", user_id: "u-pm2", from: "2024-10-07", to: "2025-06-30" },
  { project_id: "p-2502", user_id: "u-pm", from: "2025-02-17", to: "2026-08-28" },
];

/** 시드에서 그 프로젝트를 이끄는 사람 — 그 사람의 배치에 PM 표시를 단다. */
export const PM_OF = new Map(SEED.map((seed) => [seed.id, seed.pm]));

export const projectMembers: MockMember[] = TEAMS.map((team, index) => ({
  id: `member-${index + 1}`,
  project_id: team.project_id,
  user_id: team.user_id,
  assigned_on: team.from,
  released_on: team.to ?? null,
  is_pm: PM_OF.get(team.project_id) === team.user_id,
}));

/** 배치가 끝나지 않은 사람의 계획은 여기까지 그린다 — 끝없는 막대를 그릴 수는 없다. */
const PLAN_HORIZON = "2027-06-30";

/**
 * 투입 계획 — Process 화면의 배치 캡슐 하나가 이 한 행이다.
 *
 * 배치 기간을 그대로 한 칸으로 옮긴다. 사람마다 들어오는 날이 다르고 몇은 중간에 빠지므로,
 * 화면이 그릴 합류·이탈 모양이 이 데이터에 그대로 들어 있다.
 */
export const projectMemberPlans: MockMemberPlan[] = projectMembers.map((member, index) => {
  const project = projects.find((row) => row.id === member.project_id);
  const closes = member.released_on ?? project?.ended_on ?? PLAN_HORIZON;
  return {
    id: `plan-${index + 1}`,
    project_member_id: member.id,
    period_from: member.assigned_on,
    period_to: closes < member.assigned_on ? member.assigned_on : closes,
    daily_hours: PM_OF.get(member.project_id) === member.user_id ? "4.00" : "2.00",
    note: PM_OF.get(member.project_id) === member.user_id ? "총괄" : "",
  };
});

/** 그 프로젝트의 단계별 용역비 — 계약 시드가 읽는다. */
export const phaseFeesOf = (projectId: string) =>
  Object.entries(SETUP[projectId]?.fee ?? {}).map(([name, fee]) => ({
    phase_id: phaseId(projectId, name),
    fee: fee ?? 0,
    memo: "",
  }));

/** 그날 기준 7일 안에 다가오는 예정 기록 — Daily 시드의 한 일 문구를 고르는 데 쓴다. */
export function plannedLogNear(projectId: string, date: string): string | null {
  const limit = new Date(Date.parse(`${date}T00:00:00Z`) + 7 * 86_400_000)
    .toISOString()
    .slice(0, 10);
  const phase = projectPhases.find(
    (row) =>
      row.project_id === projectId &&
      row.logs.some(
        (log) => log.status === "planned" && log.log_date >= date && log.log_date <= limit
      )
  );
  return (
    phase?.logs.find(
      (log) => log.status === "planned" && log.log_date >= date && log.log_date <= limit
    )?.text ?? null
  );
}
